import VirtualScroller from "/WebInterface/new-ui/modules/virtualscroll.js";
import Dropdown from "/WebInterface/new-ui/modules/dropdown/dropdown.js";
import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";
import C from "/WebInterface/new-ui/assets/js/utils/log.js";
import {
  uploadFileCardTemplate,
  uploadFileButtonContainerTemplate,
  globalFileCardTemplate,
} from "/WebInterface/new-ui/upload/js/template.js";
import store from "/WebInterface/new-ui/upload/js/reducer.js";
import setLocale from "/WebInterface/new-ui/upload/js/localization.js";
import { doFilesExists } from "/WebInterface/new-ui/upload/utils/apis.js";
import { UploadFileCardTemplateTexts } from "/WebInterface/new-ui/upload/utils/constants.js";
import { t } from "/WebInterface/new-ui/modules/localizations/localizations.js";
import uploadingQueue from "/WebInterface/new-ui/upload/js/uploading-queue.js";
import { batchComplete } from "/WebInterface/new-ui/assets/js/utils/api/apis.js";
import Loader from "/WebInterface/new-ui/modules/loader/loader.js";
import { Confirm } from "/WebInterface/new-ui/modules/alert.js";
import { apiCall } from "/WebInterface/new-ui/assets/js/utils/api/apis.js";

let virtualScrollerList;
const maxFilesAllowedInQueue = invoke("getUserCustomizationValue", [
  "maxFilesInQueue",
]);
let isMaxFileErrorActive = false;
let uploadTypeRanges = null;
let queueCompleted = false;

const getTransferType = (fileSize) => {
  if (uploadTypeRanges) {
    const uploadType = utils.getTransferTypeForFileUsingRange(
      uploadTypeRanges,
      fileSize
    );
    return uploadType;
  }

  const uploadTypeCustomization = invoke("getUserCustomizationValue", [
    "uploadTypeCustomization",
  ]);

  uploadTypeRanges = utils.getFileTransferTypesRanges(uploadTypeCustomization);
  if (!uploadTypeRanges) {
    return null;
  }

  const uploadType = utils.getTransferTypeForFileUsingRange(
    uploadTypeRanges,
    fileSize
  );
  return uploadType;
};

const convertFilesObjectToList = (fileObj) => {
  const filesArray = Array.from(fileObj || []);
  const manipulatedArray = filesArray.map((file) => {
    return { file };
  });
  return manipulatedArray;
};

// To invoke parent methods
function invoke(method, params) {
  if (parent.window[method]) {
    return parent.window[method].apply(this, params);
  }
}

const getUploadItemIcon = (file) => {
  if (file.isDir) {
    return '<img src="/WebInterface/new-ui/assets/images/folder.png" alt="folder-img" class="media-image"/>';
  }

  const fileExtension = utils.isImageExtension(file);
  const iconPath = fileExtension || "file";

  return `<img src="/WebInterface/new-ui/assets/images/file-extension-icons/${iconPath}-extension.png" alt="${iconPath}-img" />`;
};

const getListingError = (error = {}) => {
  if (Object.keys(error).length === 0) return false;
  return (
    error.maxFiles ||
    error.fileTypeNotAllowed ||
    error.zeroByteFileNotAllowed ||
    error.fileExistInListing ||
    error.fileExistOnServer
  );
};

const getUploadCompletionMessage = (file) => {
  const { time, status, size } = file;

  if (status !== "done") {
    return false;
  }

  const timeInSec = time / 1000;
  const timeTaken =
    timeInSec < 1
      ? `${timeInSec.toFixed(1)} secs`
      : utils.formatTime({
          secs: timeInSec,
          minLabel: "m",
          secondsLabel: "s",
        });

  const averageSpeed = size / timeInSec;
  const averageUploadingSpeed = utils.formatBytes(averageSpeed);

  return {
    timeTaken,
    averageUploadingSpeed: `${averageUploadingSpeed}/s`,
    message: t("uploadedInSecAtAnAverageSpeed", {
      time: timeTaken,
      speed: `${averageUploadingSpeed}/s`,
    }),
  };
};

const getProgressBarStatus = (file) => {
  const { status, uploadedBytes = 0, size } = file;

  let progressBarStatus = {};

  const updatedValue = parseInt(uploadedBytes);

  progressBarStatus.uploadedBytesFormatted = utils.formatBytes(updatedValue);
  progressBarStatus.uploadedBytes = updatedValue;
  progressBarStatus.completedPercentage = `${Math.ceil(
    (updatedValue / size) * 100
  )} %`;

  if (
    status === "uploading" ||
    status === "paused" ||
    status === "pausing" ||
    status === "canceling" ||
    status === "finalizing"
  ) {
    progressBarStatus.showProgressBar = true;
  }

  return progressBarStatus;
};

const getUploadTimeStatus = (file) => {
  const { speed, elapsedTime, timeRemaining, overAllSpeed } = file;

  const uploadingSpeed = utils.formatBytes(speed);
  const _elapsedTime = utils.formatTime({
    secs: elapsedTime,
    minLabel: "m",
    secondsLabel: "s",
  });
  const _timeRemaining = timeRemaining
    ? utils.formatTime({
        secs: timeRemaining,
        minLabel: "m",
        secondsLabel: "s",
      })
    : "-";
  const _overAllSpeed = utils.formatBytes(
    Number.isFinite(overAllSpeed) ? overAllSpeed : 0
  );

  return {
    speed: `${uploadingSpeed}/s`,
    elapsedTime: _elapsedTime,
    timeRemaining: `${_timeRemaining}`,
    overAllSpeed: `${_overAllSpeed || 0}/s`,
  };
};

const getUploadingError = (status, uploadingError) => {
  let error = "";
  switch (status) {
    case "canceled":
      error = t("canceled");
      break;

    case "failed":
      error = uploadingError || t("failed");
      break;

    default:
      break;
  }

  return error;
};

const totalFileStatus = {
  setSelectedFiles: ({
    totalCheckedFiles,
    totalCheckedFilesSize,
    shouldHide,
  }) => {
    const totalSelectedFilesEl = _$("#totalSelectedFiles");
    const totalSelectedFilesSizeEl = _$("#totalSelectedFilesSize");
    const uploadSelectedInfoEl = _$("#uploadSelectedInfo");

    if (shouldHide) {
      totalSelectedFilesEl.text(0);
      totalSelectedFilesSizeEl.text(utils.formatBytes(0));
      totalSelectedFilesSizeEl.attr("bytes", 0);
      uploadSelectedInfoEl.addClass("is-hidden");
    } else {
      totalSelectedFilesEl.text(totalCheckedFiles);
      totalSelectedFilesSizeEl.text(utils.formatBytes(totalCheckedFilesSize));
      totalSelectedFilesSizeEl.attr("bytes", totalCheckedFilesSize);

      if (totalCheckedFiles > 0) {
        uploadSelectedInfoEl.removeClass("is-hidden");
      } else {
        _$("#checkAll").el().checked = false;
        uploadSelectedInfoEl.addClass("is-hidden");
      }
    }
  },

  getTotalSelectedFiles: () => {
    return parseInt(_$("#totalSelectedFiles").text());
  },

  getTotalSelectedFilesSize: () => {
    return parseInt(_$("#totalSelectedFilesSize").attr("bytes"));
  },

  setTotalFilesFailed: (increment) => {
    const totalFailedFilesEl = _$("#totalFailedFiles");
    const failedFilesInfoEl = _$("#failedFilesInfo");
    const totalFiles = parseInt(totalFailedFilesEl.text()) || 0;

    const totalFilesFailedCount = _$(
      "#failedFilesInfo span[data-i18n-key='totalNoFilesFailed']"
    );
    totalFilesFailedCount.text(
      t("totalNoFilesFailed", { count: totalFiles + 1 })
    );
    totalFilesFailedCount.attr(
      "data-i18n-opt",
      `{ "count": ${totalFiles + 1} }`
    );

    if (totalFiles + increment < 1) {
      totalFailedFilesEl.text(0);
      failedFilesInfoEl.addClass("is-hidden");
    } else {
      totalFailedFilesEl.text(totalFiles + increment);
      failedFilesInfoEl.removeClass("is-hidden");
    }
  },
};

const getButtonsToShowOnFileCard = (status) => {
  const hideStartButtonOnIndividualUploadItem = invoke(
    "getUserCustomizationValue",
    ["hideStartButtonOnIndividualUploadItem"]
  );

  const showResume = invoke("getUserCustomizationValue", ["showResume", true]);
  let enableShowResume = true;
  if (
    showResume !== null &&
    showResume !== undefined &&
    showResume === "false"
  ) {
    enableShowResume = false;
  }
  const selectedToResumeUploadByDefault = invoke("getUserCustomizationValue", [
    "ResumeUploadSelectedByDefault",
    true,
  ]);
  const showOverWriteButton = selectedToResumeUploadByDefault
    ? selectedToResumeUploadByDefault === "false"
    : true;

  const cardButtons = {
    fileSizeExceeded: {
      overWriteButton: !enableShowResume ? true : showOverWriteButton,
      resumeButton: enableShowResume,
      removeButton: true,
    },
    listingError: {
      overWriteButton: !enableShowResume ? true : showOverWriteButton,
      resumeButton: enableShowResume,
      removeButton: true,
    },
    restrictionError: {
      removeButton: true,
    },
    fileSizeExceededInit: {
      removeButton: true,
    },
    init: {
      uploadButton: !hideStartButtonOnIndividualUploadItem,
      removeButton: true,
    },
    queued: {
      cancelButton: true,
      waitingButton: true,
    },
    uploading: {
      pauseButton: true,
      cancelButton: true,
    },
    done: {
      reUploadButton: true,
      removeButton: true,
    },
    paused: {
      resumePauseButton: true,
      cancelButton: true,
    },
    canceled: {
      reUploadButton: true,
      removeButton: true,
    },
    failed: {
      reUploadButton: true,
      removeButton: true,
    },
    canceling: {
      cancelingButton: true,
    },
    finalizing: {
      finalizingButton: true,
    },
    pausing: {
      pausingButton: true,
      cancelButton: true,
    },
    fileTypeNotAllowed: {
      removeButton: true,
    },
    zeroByteFileNotAllowed: {
      removeButton: true,
    },
  };

  return cardButtons[status] || {};
};

const removeFileFromQueue = (file) => {
  if (file.status === "uploading" || file.status === "paused") return;

  if (file.status === "queued")
    uploadingQueue.removeFileFromUploadingQueue(file);

  store.dispatch("removeFileFromUploadFileListing", file);
  return true;
};

const overwriteFileInQueue = (file) => {
  const { id } = file;

  const curFile = store.fileList[id];

  let files = {};

  if (curFile.status === "fileSizeExceeded") {
    files[id] = {
      ...curFile,
      listingError: {
        fileExistInListing: "Storage Quota Exceeded.",
      },
      status: "fileSizeExceededInit",
    };
  } else files[id] = { ...curFile, listingError: {}, status: "init" };

  store.dispatch("updateFileStatusAndRerenderFile", files);
};

const resumeFileInQueue = (file) => {
  const { id } = file;

  const curFile = store.fileList[id];

  let files = {};
  if (curFile.status === "fileSizeExceeded") {
    files[id] = {
      ...curFile,
      listingError: {
        fileExistInListing: "Storage Quota Exceeded.",
      },
      status: "fileSizeExceededInit",
    };
  } else {
    files[id] = { ...curFile, listingError: {}, resumed: true, status: "init" };
  }

  store.dispatch("updateFileStatusAndRerenderFile", files);
};

// validate uploading file
const validateFile = (file, error, isFileSizeExceeded) => {
  const uploadedFiles = Object.values(store.fileList);
  let listingError = {};

  const fileExists = (path, id) => {
    return uploadedFiles.some((uploadedFile) => {
      return id
        ? uploadedFile.id !== id && uploadedFile.nameF === path
        : uploadedFile.nameF === path;
    });
  };

  const fileExistOnServer = (name, _path) => {
    if (!name || !_path) return false;
    name = name.toLowerCase();

    let destinationPath;
    let hash = invoke("currentHash");

    try {
      destinationPath = utils.decodeURILocal(hash.toString().replace("#", ""));
    } catch (ex) {
      destinationPath = unescape(hash.toString().replace("#", ""));
    }

    // let tempExtension = window.temp_upload_ext;

    destinationPath = destinationPath || "/";
    if (_path) destinationPath = _path;
    if (_path !== destinationPath) return false;

    let fileAvl = false;
    let curTreeItems = invoke("getCurrentTreeItems");

    if (curTreeItems) {
      fileAvl = curTreeItems.some((item) => {
        return item.name.trim().toLowerCase() === name.trim();
        // || item_name.trim().toLowerCase() === name.trim() + "" + tempExtension
      });
    }

    return fileAvl;
  };

  const { name, nameF } = file;

  // check if the file already added to queue
  const isFileAlreadyExist = fileExists(nameF);
  const isFileExistOnServer = error || fileExistOnServer(name, nameF);

  if (isFileAlreadyExist) {
    listingError.fileExistInListing =
      "File already added to upload at the same location.";

    if (isFileSizeExceeded)
      listingError.fileExistInListing += " Storage Quota Exceeded.";
  }

  if (isFileExistOnServer) {
    listingError.fileExistOnServer =
      "File with the same name is available on the server.";

    if (isFileSizeExceeded)
      listingError.fileExistOnServer += " Storage Quota Exceeded.";
  }

  return listingError;
};

const handleCustomCommonFileUploadFormOnFileAdd = (actualFileList) => {
  const uploadFileList = Object.values(actualFileList);

  const onCancel = (uploadFormModal) => {
    const cancelConfirm = window.confirm(
      t("areYouSureYouWishToCancelUploadForLastSelectedItems", {
        length: uploadFileList.length,
      })
    );

    uploadFileList.forEach((f) => {
      removeFileFromQueue(f);
    });

    if (cancelConfirm) uploadFormModal.hide(true);
  };

  const onSuccess = (formData) => {
    store.dispatch("setCustomCommonUploadFormValues", formData);

    const isCustomUploadFormAvailable = invoke("isCustomUploadFormAvailable");
    if (isCustomUploadFormAvailable) {
      handleCustomFileUploadFormOnFileAdd(actualFileList);
    } else {
      handleUpdateUploadFileListing();
    }
  };

  const formValues = store?.customCommonUploadFormValues;
  invoke("showCommonUploadForm", [{ formValues, onCancel, onSuccess }]);
};

const handleCustomFileUploadFormOnFileAdd = (fileList) => {
  const uploadFileList = Object.values(fileList).filter(
    (f) =>
      f.status !== "fileSizeExceeded" && f.status !== "fileSizeExceededInit"
  );

  let index = 0;
  let file = uploadFileList[index];

  if (!file) return;

  const next = () => {
    if (index >= uploadFileList.length - 1) return;
    index++;
    const uploadFile = uploadFileList[index];
    return uploadFile;
  };

  const prev = () => {
    if (index < 1) return;
    index--;
    const uploadFile = uploadFileList[index];
    return uploadFile;
  };

  const onSuccess = (
    uploadFormModal,
    uploadFormDetails,
    doNotShowRemainingFiles
  ) => {
    const validateFields = invoke("validate", [uploadFormModal.root, false]);

    if (validateFields.length === 0) {
      const data = utils.serializeInputs(uploadFormModal.root);

      const customFormData = {
        ...data,
        meta_form_name: uploadFormDetails?.name,
        meta_UploadFormId: uploadFormDetails?.id,
        meta_UploadFormName: uploadFormDetails?.name,
        meta_unique_upload_id: file?.id,
      };

      store.dispatch("updateFileStatus", {
        [file.id]: {
          ...file,
          customFormData,
        },
      });

      file = next();

      if (doNotShowRemainingFiles) {
        while (file) {
          store.dispatch("updateFileStatus", {
            [file.id]: {
              ...file,
              customFormData,
            },
          });
          file = next();
        }

        handleUpdateUploadFileListing();
        uploadFormModal.hide(true);
        return;
      }

      if (file) {
        const customFormData = store.fileList[file.id].customFormData;
        invoke("updateCustomUploadFormModal", [
          uploadFormModal,
          file,
          customFormData,
        ]);
        return;
      }

      handleUpdateUploadFileListing();
      uploadFormModal.hide(true);
    }
  };

  const onCancel = (uploadFormModal) => {
    uploadFileList.forEach((f) => {
      if (!store?.fileList[f.id]?.customFormData) removeFileFromQueue(f);
    });
    handleUpdateUploadFileListing();
    uploadFormModal.hide(true);
  };

  const onPrev = ({ uploadFormModal }) => {
    const prevFile = prev();

    if (prevFile) {
      const data = utils.serializeInputs(uploadFormModal.root);

      store.dispatch("updateFileStatus", {
        [file.id]: {
          ...file,
          customFormData: {
            ...data,
          },
        },
      });

      file = prevFile;
      const customFormData = store.fileList[file.id].customFormData;

      invoke("updateCustomUploadFormModal", [
        uploadFormModal,
        file,
        customFormData,
      ]);
      return;
    }
  };

  const onNext = ({ uploadFormModal }) => {
    const nextFile = next();

    if (nextFile) {
      const data = utils.serializeInputs(uploadFormModal.root);

      store.dispatch("updateFileStatus", {
        [file.id]: {
          ...file,
          customFormData: {
            ...data,
          },
        },
      });

      file = nextFile;
      const customFormData = store.fileList[file.id].customFormData;

      invoke("updateCustomUploadFormModal", [
        uploadFormModal,
        file,
        customFormData,
      ]);
      return;
    }
  };

  invoke("showCustomUploadFormModal", [
    {
      onSuccess,
      onCancel,
      onPrev,
      onNext,
      uploadFile: file,
    },
  ]);
};

const handleCustomFileUploadFormOnClickDetails = (_file) => {
  const uploadFileList = Object.values(store.fileList);

  let index = uploadFileList.findIndex((f) => f.id === _file.id);
  if (index < 0) index = 0;

  let file = uploadFileList[index];

  const next = () => {
    if (index >= uploadFileList.length - 1) return;
    index++;
    const uploadFile = uploadFileList[index];
    return uploadFile;
  };

  const prev = () => {
    if (index < 1) return;
    index--;
    const uploadFile = uploadFileList[index];
    return uploadFile;
  };

  const onSuccess = (uploadFormModal, uploadFormDetails) => {
    const validateFields = invoke("validate", [uploadFormModal.root, false]);

    if (validateFields.length === 0) {
      const data = utils.serializeInputs(uploadFormModal.root);

      store.dispatch("updateFileStatus", {
        [file.id]: {
          ...file,
          customFormData: {
            ...file.customFormData,
            ...data,
            meta_form_name: uploadFormDetails?.name,
            meta_UploadFormId: uploadFormDetails?.id,
            meta_UploadFormName: uploadFormDetails?.name,
            meta_unique_upload_id: file?.id,
          },
        },
      });

      uploadFormModal.hide(true);
    }
  };

  const onCancel = (uploadFormModal) => {
    uploadFormModal.hide(true);
  };

  const onPrev = ({ uploadFormModal }) => {
    const prevFile = prev();

    if (prevFile) {
      const data = utils.serializeInputs(uploadFormModal.root);

      store.dispatch("updateFileStatus", {
        [file.id]: {
          ...file,
          customFormData: {
            ...data,
          },
        },
      });

      file = prevFile;
      const customFormData = store.fileList[file.id].customFormData;

      invoke("updateCustomUploadFormModal", [
        uploadFormModal,
        file,
        customFormData,
      ]);
      return;
    }
  };

  const onNext = ({ uploadFormModal }) => {
    const nextFile = next();

    if (nextFile) {
      const data = utils.serializeInputs(uploadFormModal.root);

      store.dispatch("updateFileStatus", {
        [file.id]: {
          ...file,
          customFormData: {
            ...data,
          },
        },
      });

      file = nextFile;
      const customFormData = store.fileList[file.id].customFormData;

      invoke("updateCustomUploadFormModal", [
        uploadFormModal,
        file,
        customFormData,
      ]);
      return;
    }
  };

  const onAfterShow = ({ uploadFormModal }) => {
    const customFormData = store.fileList[file.id].customFormData;
    invoke("updateCustomForm", [customFormData, uploadFormModal]);
  };

  invoke("showCustomUploadFormModal", [
    {
      onAfterShow,
      onSuccess,
      onCancel,
      onPrev,
      onNext,
      uploadFile: file,
    },
  ]);
};

// rename file
const renameFile = (name, fullPath) => {
  let path = invoke("currentPath");
  let _fullPath = fullPath.substring(1);
  return path + `${_fullPath ? `${_fullPath}/` : ""}` + name;
};

const addFilesToQueue = async (fileList = []) => {
  const checkAllCheckboxChecked = _$("#checkAll").el().checked;

  const maxUploadItemsWarningMessage = invoke("getUserCustomizationValue", [
    "maxUploadItemsWarningMessage",
    true,
  ]);
  const maxUploadItemsBeforeShowingWarning = invoke(
    "getUserCustomizationValue",
    ["maxUploadItemsBeforeShowingWarning", true]
  );

  if (parseInt(maxUploadItemsBeforeShowingWarning) < fileList.length) {
    invoke("growl", [
      maxUploadItemsWarningMessage || t("largeUploadListingDefaultWarning"),
      t("warning"),
      null,
      true,
    ]);
  }

  let actualFileList = {};

  fileList.forEach((fileObj) => {
    const file = fileObj?.file;
    const fullPath = fileObj?.fullPath || "/";
    const error = fileObj?.exists;

    const extension = utils.getFileExtension(file);

    const { isDir } = file;
    const nameF = window?.init_href_path
      ? `${window.init_href_path}/${file.name}`
      : renameFile(file.name, fullPath);
    const size = file.size;
    const sizeF = utils.formatBytes(size);

    const customDateFormat =
      invoke("getUserCustomizationValue", ["DATE_FORMAT_TEXT", true]) ||
      "m/d/Y";
    const customTimeFormat =
      invoke("getUserCustomizationValue", ["TIME_FORMAT_TEXT", true]) ||
      "G:i:S K";

    let dateFormat = "";
    dateFormat = utils.dateStringFormat({ format: customDateFormat });
    dateFormat += ` ${utils.dateStringFormat({
      format: customTimeFormat,
    })}`;
    const lastModifiedF = flatpickr.formatDate(
      new Date(file.lastModified),
      dateFormat
    );

    const id = utils.getUId();

    const canUseWebSocketForUploading = invoke("getUserCustomizationValue", [
      "useWSForAdvancedUploads",
    ]);

    const uploadTypeByRangeCustomization = getTransferType(size);
    const uploadTypeBySimpleCustomization = canUseWebSocketForUploading
      ? "WS"
      : "Normal";

    const obj = {
      id,
      isDir,
      file,
      nameF,
      sizeF,
      lastModifiedF,
      name: file.name,
      lastModified: file.lastModified,
      lastModifiedDate: file.lastModifiedDate,
      type: file.type,
      size: size,
      uploadType:
        uploadTypeByRangeCustomization || uploadTypeBySimpleCustomization,
      speed: 0,
      elapsedTime: 0,
      checked: checkAllCheckboxChecked,
    };

    const disableZeroByteUpload = invoke("getUserCustomizationValue", [
      "disableZeroByteUpload",
    ]);
    if (disableZeroByteUpload && size <= 0) {
      actualFileList[id] = {
        ...obj,
        status: "zeroByteFileNotAllowed",
        listingError: { zeroByteFileNotAllowed: t("zeroByteFileNotAllowed") },
      };
      return;
    }

    const allowedExtensionsToUpload = invoke("getUserCustomizationValue", [
      "onlyAllowedExtensionsToUpload",
      true,
    ]);
    if (allowedExtensionsToUpload) {
      const isExtensionAllow = allowedExtensionsToUpload
        .toLowerCase()
        .split(",")
        .some(
          (ext) => ext.trim() === extension || ext.trim() === `.${extension}`
        );

      if (!isExtensionAllow) {
        actualFileList[id] = {
          ...obj,
          status: "fileTypeNotAllowed",
          listingError: { fileTypeNotAllowed: t("fileTypeNotAllowed") },
        };
        return;
      }
    }

    const notAllowedExtensionsToUpload = invoke("getUserCustomizationValue", [
      "notAllowedExtensionsToUpload",
      true,
    ]);
    if (notAllowedExtensionsToUpload) {
      const isExtensionNotAllow = notAllowedExtensionsToUpload
        .toLowerCase()
        .split(",")
        .some(
          (ext) => ext.trim() === extension || ext.trim() === `.${extension}`
        );

      if (isExtensionNotAllow) {
        actualFileList[id] = {
          ...obj,
          status: "fileTypeNotAllowed",
          listingError: { fileTypeNotAllowed: t("fileTypeNotAllowed") },
        };
        return;
      }
    }

    const currentDirInfo = invoke("getCurrentDirInfo");
    const isFileSizeExceededFromQuota =
      parseInt(currentDirInfo?.quota_bytes) <= size;
    const listingError = validateFile(obj, error, isFileSizeExceededFromQuota);
    actualFileList[id] = {
      ...obj,
      status: Object.keys(listingError).length > 0 ? "listingError" : "init",
      listingError,
    };

    if (isFileSizeExceededFromQuota && Object.keys(listingError).length > 0)
      actualFileList[id].status = "fileSizeExceeded";
    else if (isFileSizeExceededFromQuota) {
      actualFileList[id].status = "fileSizeExceededInit";
      actualFileList[id].listingError = {
        fileTypeNotAllowed: "Storage Quota Exceeded.",
      };
    }
  });

  store.dispatch("addFileToUploadFileList", { ...actualFileList });
  window.init_href_path = null;

  let actualFileListArray = Object.values(actualFileList);
  actualFileListArray = actualFileListArray.filter(
    (actualFile) =>
      actualFile.status !== "fileTypeNotAllowed" ||
      actualFile.status !== "zeroByteFileNotAllowed"
  );

  if (actualFileListArray.length > 0) {
    const actualFileListObject = actualFileListArray.reduce(
      (accumulator, currentValue) => ({
        ...accumulator,
        [currentValue.id]: currentValue,
      }),
      {}
    );

    const commonUploadForm = invoke("commonUploadForm");

    const commonFormPerBatch =
      invoke("getUserCustomizationValue", ["commonFormPerBatch"]) &&
      queueCompleted;

    if (
      commonUploadForm?.formDetails &&
      (commonFormPerBatch || !store?.customCommonUploadFormValues)
    ) {
      queueCompleted = false;
      handleCustomCommonFileUploadFormOnFileAdd(actualFileListObject);
      return;
    }

    const isCustomUploadFormAvailable = invoke("isCustomUploadFormAvailable");
    if (isCustomUploadFormAvailable)
      handleCustomFileUploadFormOnFileAdd(actualFileListObject);
  }
};

const getSharingFiles = (getSelected = false) => {
  const fileList = Object.values(store.fileList);
  const filesToShare = [];

  fileList.forEach((file) => {
    if (file.status === "done") {
      if (getSelected) {
        if (file.checked) filesToShare.push(file.nameF);
        return;
      }

      filesToShare.push(file.nameF);
    }
  });

  const files =
    filesToShare.length > 5
      ? filesToShare.slice(0, 5).join("\r\n") +
        `\r\n(and ${filesToShare.length - 5} more...)`
      : filesToShare.join("\r\n");

  const paths = filesToShare.join("\r\n");
  const filesLength = filesToShare.length;

  return { files, paths, filesLength };
};

// Upload modal header buttons events
const uploadHeaderButtonsEvents = () => {
  const clearFilter = () => {
    const fileList = Object.values(store.fileList);
    let allFiles = {};

    _$("#search").val("");
    const checkAllCheckbox = _$("#checkAll").el();
    checkAllCheckbox.checked = false;

    fileList.forEach((file) => {
      delete file.hide;
      allFiles[file.id] = { ...file, checked: false };
    });

    _$("#filter-applied-button,#filteredText").addClass("is-hidden");

    store.dispatch("updateFileStatusInFileList", allFiles);
    handleUpdateUploadFileListing();
  };

  _$("#addFilesButtonUploadModal").on("click", () => {
    _$("#fileInputUploadModal").el().click();
  });

  _$("#uploadFileButton").on("click", () => {
    _$("#fileInputUploadModal").el().click();
  });

  _$("#uploadAllButtonUploadModal").on("click", () => {
    if (isMaxFileErrorActive) return;
    const fileList = Object.values(store.fileList);
    const filteredFiles = fileList.filter(
      (file) => file.status === "init" && !file.hide
    );
    filteredFiles.forEach((file) => {
      uploadingQueue.addFileToUploadingQueue(file);
    });

    if (filteredFiles?.length > 0) {
      const parentPostUploadConfig = {
        type: "uploadStarted",
        mode: "all-files",
        files: filteredFiles.map((file) => ({
          name: file.name,
          uploading_to: file.nameF,
          size: file.size,
          status: "",
        })),
      };

      invoke("uploadNotifications", [parentPostUploadConfig]);
    }
  });

  _$("#reUploadAllButtonUploadModal").on("click", async () => {
    if (isMaxFileErrorActive) return;

    const resp = await Confirm({
      title: `<span data-i18n-key="reUploadAll">${t("reUploadAll")}</span>`,
      body: `<span data-i18n-key="reuploadConfirmationMessage">${t(
        "reuploadConfirmationMessage"
      )}</span>`,
      closable: true,
      confirm: `<span data-i18n-key="yes">${t("yes")}</span>`,
      cancel: `<span data-i18n-key="no">${t("no")}</span>`,
    });

    if (!resp) {
      return;
    }

    const fileList = Object.values(store.fileList);
    const filteredFiles = fileList.filter(
      (file) => file.status === "done" || file.status === "canceled"
    );

    filteredFiles.forEach((file) => {
      uploadingQueue.addFileToUploadingQueue(file);
    });

    if (filteredFiles?.length > 0) {
      const parentPostUploadConfig = {
        type: "uploadStarted",
        mode: "all-files",
        files: filteredFiles.map((file) => ({
          name: file.name,
          uploading_to: file.nameF,
          size: file.size,
          status: "",
        })),
      };

      invoke("uploadNotifications", [parentPostUploadConfig]);
    }
  });

  _$("#uploadSelectedButtonUploadModal").on("click", () => {
    if (isMaxFileErrorActive) return;
    const fileList = Object.values(store.fileList);
    const filteredFiles = fileList.filter(
      (file) => file.status === "init" && file.checked
    );

    filteredFiles.forEach((file) => {
      uploadingQueue.addFileToUploadingQueue(file);
    });

    if (filteredFiles?.length > 0) {
      const parentPostUploadConfig = {
        type: "uploadStarted",
        mode: "selected-files",
        files: filteredFiles.map((file) => ({
          name: file.name,
          uploading_to: file.nameF,
          size: file.size,
          status: "",
        })),
      };

      invoke("uploadNotifications", [parentPostUploadConfig]);
    }
  });

  _$("#cancelUploadingButtonUploadModal").on("click", () => {
    if (uploadingQueue.queue.length < 1) return;

    const canceled = window.confirm(t("areYouSureYouWantToCancelUploading"));

    if (canceled) {
      uploadingQueue.cancel(true);
    }
  });

  _$("#commonUploadFormButton").on("click", () => {
    const formValues = store?.customCommonUploadFormValues;

    const onSuccess = (formData) => {
      store.dispatch("setCustomCommonUploadFormValues", formData);
    };

    invoke("showCommonUploadForm", [
      {
        formValues,
        onSuccess,
        onCancel: (uploadFormModal) => {
          uploadFormModal?.hide(true);
        },
      },
    ]);
  });

  _$("#overwriteAllButtonUploadModal").on("click", () => {
    if (isMaxFileErrorActive) return;

    const fileList = Object.values(store.fileList);
    let updatedFiles = {};

    fileList.forEach((file) => {
      if (file.status === "fileSizeExceeded") {
        updatedFiles[file.id] = {
          ...file,
          listingError: {
            fileExistInListing: "Storage Quota Exceeded.",
          },
          status: "fileSizeExceededInit",
        };
      }
      if (file.status === "listingError") {
        updatedFiles[file.id] = { ...file, status: "init", listingError: {} };
      }
    });
    store.dispatch("updateFileStatusAndRerenderFile", updatedFiles);
  });

  _$("#resumeAllButtonUploadModal").on("click", () => {
    if (isMaxFileErrorActive) return;

    const fileList = Object.values(store.fileList);
    let updatedFiles = {};

    fileList.forEach((file) => {
      if (file.status === "fileSizeExceeded") {
        updatedFiles[file.id] = {
          ...file,
          listingError: {
            fileExistInListing: "Storage Quota Exceeded.",
          },
          status: "fileSizeExceededInit",
        };
      }
      if (file.status === "listingError") {
        updatedFiles[file.id] = {
          ...file,
          resume: true,
          status: "init",
          listingError: {},
        };
      }
    });
    store.dispatch("updateFileStatusAndRerenderFile", updatedFiles);
  });

  _$("#checkAll").on("click", (e) => {
    const fileList = Object.values(store.fileList);
    let updatedFiles = {};

    let totalCheckedFilesSize = 0;
    let totalCheckedFiles = 0;

    fileList.forEach((file) => {
      if (file.hide) return;

      const card = _$(`#${file.id}`);
      if (card.el()) {
        card.find(".checkbox", true).checked = e.target.checked;
      }

      totalCheckedFiles++;
      totalCheckedFilesSize = totalCheckedFilesSize + file.size;
      updatedFiles[file.id] = { ...file, checked: e.target.checked };
    });

    totalFileStatus.setSelectedFiles({
      totalCheckedFiles,
      totalCheckedFilesSize,
      shouldHide: totalCheckedFilesSize && e.target.checked ? false : true,
    });

    store.dispatch("updateFileStatusInFileList", updatedFiles);
  });

  const removeFiles = (status) => {
    store.dispatch("removeMultipleFilesFromUploadFileListing", { status });
  };

  _$("#removeAllSelectedButtonUploadModal").on("click", () => {
    removeFiles("checked");
  });

  _$("#removeAllWithErrorButtonUploadModal").on("click", () => {
    if (isMaxFileErrorActive) {
      removeFiles();
      return;
    }
    removeFiles("listingError");
    removeFiles("fileSizeExceeded");
    removeFiles("fileSizeExceededInit");
    removeFiles("zeroByteFileNotAllowed");
  });

  _$("#removeAllUploadedButtonUploadModal").on("click", () => {
    removeFiles("done");
  });

  _$("#removeAllCanceledButtonUploadModal").on("click", () => {
    removeFiles("canceled");
  });

  _$("#removeAllSkippedButtonUploadModal").on("click", () => {
    removeFiles("skipped");
  });

  _$("#removeAllButtonUploadModal").on("click", () => {
    removeFiles();
  });

  _$("#shareUploadedButtonUploadModal").on("click", () => {
    const files = getSharingFiles();
    if (files.filesLength) invoke("initSharing", [files]);
  });

  _$("#shareSelectedButtonUploadModal").on("click", () => {
    const files = getSharingFiles(true);
    if (files.filesLength) invoke("initSharing", [files]);
  });

  _$("#filter-applied-button").on("click", () => {
    clearFilter();
  });

  _$("#search").on("input", (e) => {
    const { value } = e.target;

    if (!value) {
      clearFilter();
      return;
    }

    const fileList = Object.values(store.fileList);
    let filteredFiles = {};
    let allFiles = {};

    fileList.forEach((file) => {
      delete file.hide;

      let hide = true;

      if (file.nameF.toLowerCase().includes(value.toLowerCase())) {
        filteredFiles[file.id] = { ...file, checked: false };
        hide = false;
      }

      allFiles[file.id] = { ...file, checked: false, hide };
    });

    _$("#filter-applied-button,#filteredText").removeClass("is-hidden");
    const checkAllCheckbox = _$("#checkAll").el();
    checkAllCheckbox.checked = false;

    store.dispatch("updateFileStatusInFileList", allFiles);
    handleUpdateUploadFileListing();
  });
};

// upload modal hidden file input event
_$("#fileInputUploadModal").on("change", (e) => {
  if (uploadingQueue.queue.length > 0 && isMaxFileErrorActive) {
    invoke("growl", [
      t("maxNumberOfFilesExceeded"),
      t("maxFileAllowedTouploadExceeded", { maxFilesAllowedInQueue }),
      4000,
      true,
    ]);
    return;
  }

  const fileObj = e.target.files;
  const filesToUpload = convertFilesObjectToList(fileObj);
  addFilesToQueue(filesToUpload);

  //Remove file selection as its no longer required
  _$("#fileInputUploadModal").val("");
});

// Drag and drop file event
const uploadDragAndDropEvents = () => {
  let isDraggingCrushFTPItem = false;

  window.addEventListener("dragstart", () => {
    isDraggingCrushFTPItem = true;
  });

  window.addEventListener("dragend", (e) => {
    e.preventDefault();
    isDraggingCrushFTPItem = false;
  });

  window.addEventListener("dragover", () => {
    if (isDraggingCrushFTPItem) {
      return;
    }

    if (!invoke("getDraggingItemIsFromCrushFTP"))
      _$("#uploadDragAndDropSection").removeClass("is-hidden");
  });

  _$("#uploadDragAndDropSection").on("dragleave", () => {
    _$("#uploadDragAndDropSection").addClass("is-hidden");
  });

  window.addEventListener("drop", () => {
    _$("#uploadDragAndDropSection").addClass("is-hidden");
  });

  _$("#uploadDragAndDropSection").on("drop", async (e) => {
    if (isDraggingCrushFTPItem) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    const privs = invoke("currentDirPrivs");
    if (!privs?.includes("(write)")) {
      invoke("growl", [
        t("youDoNotHavePermissionToUpload"),
        t("uploadNotAllowed"),
        5000,
        true,
      ]);
      _$("#uploadDragAndDropSection").addClass("is-hidden");
      return false;
    }

    const fileItems = e.dataTransfer.items;
    let filesObj = {};
    const finalFilesArray = [];
    let fileReadTimer = null;

    const currentPath = invoke("currentPath");
    let curPath = currentPath === "/" ? "" : currentPath;

    const handleDnDUpload = (fileItems, callback) => {
      if (uploadingQueue.queue.length > 0 && isMaxFileErrorActive) {
        invoke("growl", [
          t("maxNumberOfFilesExceeded"),
          t("maxFileAllowedTouploadExceeded", { maxFilesAllowedInQueue }),
          4000,
          true,
        ]);
        callback();
        return;
      }
      if (fileItems) {
        let pendingOperations = 0;
        let hasError = false;

        const processComplete = () => {
          pendingOperations--;
          if (pendingOperations === 0 && !hasError) {
            callback();
          }
        };

        for (let i = 0, item; (item = fileItems[i]); ++i) {
          const entry = item.webkitGetAsEntry();

          if (entry.isDirectory) {
            const blockDirectory = invoke("getUserCustomizationValue", [
              "blockUploadingDirs",
            ]);

            if (blockDirectory) {
              invoke("growl", [
                t("directoriesCantBeUploadedSelectOnlyFiles"),
                t("uploadingDirectoryIsNotAllowed"),
                4000,
                true,
              ]);
              continue;
            }

            pendingOperations++;
            const readDirectory = (entry) => {
              const dirReader = entry.createReader();
              let _entries = [];

              function toArray(list) {
                return Array.prototype.slice.call(list || [], 0);
              }

              const handleEntries = (entries, entry) => {
                const { fullPath } = entry;

                if (entries.length === 0) {
                  filesObj[fullPath] = {
                    file: {
                      name: "",
                      size: 0,
                      isDir: true,
                    },
                    fullPath,
                    key: fullPath,
                  };
                }
                entries.forEach((_entry) => {
                  if (_entry.isFile) {
                    pendingOperations++;
                    _entry.file(function (file) {
                      const name = file.name;
                      filesObj[`${curPath}/${fullPath}/${name}`] = {
                        file,
                        fullPath,
                        key: `${curPath}/${fullPath}/${name}`,
                      };
                      processComplete();
                    });
                  } else {
                    readDirectory(_entry);
                  }
                });
              };

              const readEntries = function () {
                dirReader.readEntries(
                  function (results) {
                    if (!results.length) {
                      handleEntries(_entries, entry);
                      processComplete();
                    } else {
                      _entries = _entries.concat(toArray(results));
                      readEntries();
                    }
                  },
                  function (error) {
                    hasError = true;
                    processComplete();
                  }
                );
              };

              readEntries();
            };

            readDirectory(entry);
          } else {
            pendingOperations++;
            entry.file(function (file) {
              const name = file.name;
              filesObj[`${curPath}/${name}`] = {
                file,
                fullPath: "/",
                key: `${curPath}/${name}`,
              };
              processComplete();
            });
          }
        }
      }
    };

    const checkFileExistsOnServer = async () => {
      const fileArr = Object.values(filesObj);
      const paths = [];

      fileArr.forEach(({ key }) => {
        paths.push(utils.encodeURILocal(key));
      });

      if (paths.length < 1) {
        addFilesToQueue(finalFilesArray);
        return;
      }

      try {
        const resp = await doFilesExists({
          paths: paths.join(";"),
          random: Math.random(),
        });

        const responseArr = utils.decodeURILocal(resp).split("\n");

        responseArr.forEach(function (item) {
          const curItem = item.replace(/\n|\r/g, "").split(":");
          const exists = curItem[1] === "true";
          let path = curItem[0];

          if (filesObj[path]) {
            const { file, fullPath } = filesObj[path];
            finalFilesArray.push({ file, fullPath, exists });
          }
          delete filesObj[path];
        });
        addFilesToQueue(finalFilesArray);
      } catch (error) {
        C.error(error);
      }
    };

    const loading = new Loader({
      parentElement: _$("#uploadFileSection").el().parentElement.parentElement,
    });

    const filesDroppedAndAdded = () => {
      checkFileExistsOnServer();
      setTimeout(() => {
        loading.deleteLoader();
      }, 100);
    };
    let droppedAllTimer;
    handleDnDUpload(fileItems, () => {
      if (droppedAllTimer) clearInterval(droppedAllTimer);
      droppedAllTimer = setTimeout(filesDroppedAndAdded, 500);
    });

    _$("#uploadDragAndDropSection").addClass("is-hidden");
  });

  _$("#uploadDragAndDropSection").on("dragover", (e) => {
    e.preventDefault();
  });
};

// Upload File Card Button Listeners
const uploadFileCardButtonsListeners = (file, card) => {
  const addButtonListener = (selector, callback) => {
    const button = _$(card).find(selector);
    button?.on("click", (e) => callback(file, e.altKey));
  };

  addButtonListener(".upload-file-card-icon-wrapper", (file, altKey) => {
    if (altKey) {
      uploadingQueue.toggleUploadType(file);
      return;
    }
    return false;
  });

  addButtonListener("#upload-button", (file, altKey) => {
    if (altKey) {
      uploadingQueue.toggleUploadType(file);
      return;
    }
    uploadingQueue.addFileToUploadingQueue(file);

    const parentPostUploadConfig = {
      type: "uploadStarted",
      mode: "selected-files",
      files: [
        {
          name: file.name,
          uploading_to: file.nameF,
          size: file.size,
          status: "",
        },
      ],
    };
    invoke("uploadNotifications", [parentPostUploadConfig]);
  });

  addButtonListener("#remove-button", (file) => {
    removeFileFromQueue(file);
  });

  addButtonListener("#overwrite-button", (file) => {
    overwriteFileInQueue(file);
  });

  addButtonListener("#detail-button", async (file) => {
    handleCustomFileUploadFormOnClickDetails(file);
  });

  addButtonListener("#resume-button", (file) => {
    resumeFileInQueue(file);
  });

  addButtonListener("#resume-pause-button", () => {
    uploadingQueue.resume();
  });

  addButtonListener("#pause-button", () => {
    uploadingQueue.pausing();
  });

  addButtonListener("#reupload-button", (file, altKey) => {
    const reUploadId = utils.getUId();

    if (altKey) {
      uploadingQueue.toggleUploadType({ ...file, reUploadId });
      return;
    }
    uploadingQueue.addFileToUploadingQueue({ ...file, reUploadId });
  });

  addButtonListener("#cancel-button", (file) => {
    const canceled = window.confirm(
      t("areYouSureYouWantToCancelParticularFileFromUploading", {
        file: file.nameF,
      })
    );

    if (!canceled) {
      return;
    }

    const curUploadingFile = uploadingQueue.getCurrentUploadingFile();

    if (file?.id && curUploadingFile?.id === file?.id) {
      uploadingQueue.cancel();
    } else {
      uploadingQueue.removeFileFromUploadingQueue(file);

      store.dispatch("updateFileStatusAndRerenderFile", {
        [file?.id]: {
          ...file,
          status: "canceled",
        },
      });
    }
  });

  const checkbox = _$(card).find(".checkbox");
  checkbox.on("click", (e) => {
    const isChecked = e.target.checked;
    store.dispatch("updateFileStatusInFileList", {
      [file.id]: { ...file, checked: isChecked },
    });

    const files = store.fileList;
    let allFilesChecked = true;
    let allFilesUnChecked = true;
    let totalCheckedFiles = 0;
    let totalCheckedFilesSize = 0;

    Object.values(files).forEach((file) => {
      if (!file.checked && _$(`#${file.id}`).el()) allFilesChecked = false;
      if (file.checked) allFilesUnChecked = false;

      if (file.checked) {
        totalCheckedFiles++;
        totalCheckedFilesSize += file.size;
      }
    });

    _$("#checkAll").el().checked = allFilesChecked;

    if (allFilesUnChecked) {
      totalFileStatus.setSelectedFiles({ shouldHide: true });
    } else {
      totalFileStatus.setSelectedFiles({
        totalCheckedFiles,
        totalCheckedFilesSize,
        shouldHide: false,
      });
    }
  });
};

const checkMaxFilesReached = () => {
  const fileList = Object.values(store.fileList);
  let maxFileError = {};

  if (
    maxFilesAllowedInQueue &&
    fileList.length > maxFilesAllowedInQueue &&
    !isMaxFileErrorActive
  ) {
    isMaxFileErrorActive = true;

    invoke("growl", [
      t("maxNumberOfFilesExceeded"),
      t("maxFileAllowedTouploadExceeded", { maxFilesAllowedInQueue }),
      4000,
      true,
    ]);
  }

  if (maxFilesAllowedInQueue && fileList.length <= maxFilesAllowedInQueue) {
    isMaxFileErrorActive = false;
  }

  if (maxFilesAllowedInQueue && fileList.length > maxFilesAllowedInQueue) {
    maxFileError.status = "restrictionError";
    maxFileError.listingError = `Maximum files allowed in a queue ${maxFilesAllowedInQueue}`;
  }

  return maxFileError;
};

const getFileList = (fileObj = {}) => {
  const fileList = Object.values(fileObj);

  let totalSize = 0;
  let totalCheckedFiles = 0;
  let totalCheckedFilesSize = 0;

  const filteredList = fileList.filter((file) => {
    if (file.hide) return false;

    totalSize += file.size;

    if (file.checked) {
      totalCheckedFiles++;
      totalCheckedFilesSize += file.size;
    }

    return true;
  });

  _$("#totalFiles").text(filteredList.length);
  _$("#totalSize").text(utils.formatBytes(totalSize));

  totalFileStatus.setSelectedFiles({
    totalCheckedFiles,
    totalCheckedFilesSize,
    shouldHide: totalCheckedFiles > 0 ? false : true,
  });

  return filteredList;
};

const reCheckFileQuota = () => {
  const fileArr = Object.values(store.fileList);
  let actualFileList = store.fileList;

  store.dispatch("addFileToUploadFileListAndRefresh", {});

  const currentDirInfo = invoke("getCurrentDirInfo");
  const quotaBytesLeft = parseInt(currentDirInfo?.quota_bytes);

  fileArr.forEach((file) => {
    const id = file.id;

    const isFileSizeExceededFromQuota = quotaBytesLeft <= file.size;
    if (isFileSizeExceededFromQuota) return;

    if (file.status === "fileSizeExceeded") {
      const listingError = validateFile(
        file,
        null,
        isFileSizeExceededFromQuota
      );

      actualFileList[id] = {
        ...file,
        status: Object.keys(listingError).length > 0 ? "listingError" : "init",
        listingError,
      };
    } else if (file.status === "fileSizeExceededInit") {
      actualFileList[id] = { ...file, listingError: {}, status: "init" };
    }
  });

  store.dispatch("addFileToUploadFileListAndRefresh", { ...actualFileList });
};

// Listing uploading files
const handleUpdateUploadFileListing = () => {
  const fileArr = Object.values(store.fileList);
  const uploadFilesSection = document.querySelector("#uploadFileSection");

  const fileList = getFileList(store.fileList);

  if (fileArr.length === 0) {
    uploadFilesSection.innerHTML = uploadFileButtonContainerTemplate({
      dragAndDropFileHereText: t("dragAndDropFileHere"),
      uploadFilesText: t("uploadFiles"),
    });
    _$("#uploadFileButton").on("click", () => {
      _$("#fileInputUploadModal").el().click();
    });
    return;
  }

  const maxFileError = checkMaxFilesReached();

  const scrollTopBeforeRerender =
    virtualScrollerList?.container?.scrollTop || 0;

  uploadFilesSection.innerHTML = "";

  virtualScrollerList.init({
    totalRows: fileList.length,
    generatorFn: (rowIndex) => {
      const { id } = fileList[rowIndex];

      const filesObj = store.fileList;
      const file = filesObj[id];

      let status = file?.status;

      const uploadFileCardWrapper = _$("<div>").el();
      uploadFileCardWrapper.id = id;
      uploadFileCardWrapper.classList.add("upload-file-card-wrapper");

      const autoStartFile = lookForAutoStartUpload(file, status);
      if (autoStartFile) {
        status = "queued";
      }

      const showCardButtons = getButtonsToShowOnFileCard(
        maxFileError.status || status
      );
      const listingError =
        maxFileError.listingError || getListingError(file.listingError);
      const uploadingError = maxFileError.status
        ? ""
        : getUploadingError(status, file.uploadingError);
      const completionDetails = maxFileError.status
        ? null
        : getUploadCompletionMessage(file);
      const progressBarStatus = maxFileError.status
        ? {}
        : getProgressBarStatus(file);

      const timeStatus = getUploadTimeStatus(file);
      const isElectronUploadOnlyApp = invoke("isElectron");

      uploadFileCardWrapper.insertAdjacentHTML(
        "beforeend",
        uploadFileCardTemplate({
          hideCheckbox: isElectronUploadOnlyApp,
          checked: file?.checked || false,
          image: getUploadItemIcon(file),
          name: file.nameF,
          sizeInBytes: file.file.size,
          size: file.sizeF,
          listingError,
          customFormData: file?.customFormData,
          lastModifiedF: file.lastModifiedF,
          chunkPreparationStalled: file?.chunkPreparationStalled,
          uploadType: file.uploadType,
          ...showCardButtons,
          ...progressBarStatus,
          ...timeStatus,
          uploadingError,
          completionMessage: completionDetails?.message,
          timeTaken: completionDetails?.timeTaken,
          averageUploadingSpeed: completionDetails?.averageUploadingSpeed,
          ...UploadFileCardTemplateTexts(),
        })
      );

      uploadFileCardButtonsListeners(file, uploadFileCardWrapper);

      return uploadFileCardWrapper;
    },
  });

  uploadFilesSection.append(virtualScrollerList.container);
  virtualScrollerList.container.scrollTop = scrollTopBeforeRerender;
};

const lookForAutoStartUpload = (file, status) => {
  const shouldStartFileUpload = invoke("checkForAutoUpload");

  if (!shouldStartFileUpload) {
    return;
  }

  const commonUploadForm = invoke("commonUploadForm");
  const isCustomUploadFormAvailable = invoke("isCustomUploadFormAvailable");

  if (commonUploadForm?.formDetails && !store?.customCommonUploadFormValues) {
    return;
  }

  if (isCustomUploadFormAvailable && !file.customFormData) {
    return;
  }

  if (status === "init") {
    uploadingQueue.addFileToUploadingQueue(file);
    return true;
  }
};

const shareFilesOnQueueFinishedElectron = async () => {
  const completedFiles = uploadingQueue.getCompletedFiles();
  const ipcRenderer = invoke("getIPCRenderer");

  const filesToShare = [];

  completedFiles.forEach((file) => {
    const fileData = store?.fileList[file.id];
    if (fileData?.status === "done") {
      return filesToShare.push(fileData?.nameF);
    }
  });

  const files = filesToShare.join("\r\n");

  const defaultEmailBody =
    "A user would like to share a file with you.  This link will expire on {date} at {time}\r\n\r\n{web_link}";
  const defaultEmailSubject = "Sharing: A new file is being shared with you.";
  const baseURL = invoke("getBaseURL");

  const obj = {
    command: "publish",
    allowUploads: false,
    attach: false,
    hide_download: false,
    baseUrl: utils.encodeURILocal(baseURL),
    emailBcc: "",
    emailReplyTo: "",
    emailBody: defaultEmailBody,
    share_comments: "",
    emailCc: "",
    emailFrom: "",
    emailSubject: defaultEmailSubject,
    emailTo: "",
    shareUsername: false,
    shareUsernames: "",
    logins: -1,
    shareUsernamePermissions: "",
    expire: `${flatpickr.formatDate(new Date(), "m/d/Y")} 23:59`,
    paths: files,
    publishType: "reference",
    sendEmail: false,
  };

  try {
    const response = await apiCall("publish", {
      ...obj,
    });

    const result = response?.commandResult?.response;

    const message = utils.decodeURILocal(result?.message);
    const url = utils.decodeURILocal(result?.url);

    const id = utils.getUId();
    const timestamp = Date.now();
    const time = flatpickr.formatDate(new Date(), "m/d/Y H:i:S K");
    const sharedItems = filesToShare;

    const shareData = {
      message,
      url,
      id,
      timestamp,
      time,
      sharedItems,
      sharedItemsPaths: files,
    };

    const userInfo = invoke("getUserInfo");
    const { username } = userInfo;
    const shareHistory =
      JSON.parse(localStorage.getItem(`${username}_share_history`)) || [];

    shareHistory.push(shareData);
    localStorage.setItem(
      `${username}_share_history`,
      JSON.stringify(shareHistory)
    );

    ipcRenderer.send("share-link", { url });
  } catch (error) {
    console.log(error);
  }
};

// File progress bar update subscribe events
const fileProgressStatus = {
  showUploadFileProgressBar: ({ fileId, size, resumeLoc = 0 }) => {
    const fileCard = _$(`#${fileId}`).el();
    if (fileCard) {
      _$(fileCard).find(".progress").attr("value", resumeLoc);
      _$(fileCard).find(".progress").attr("max", size);

      const maxValue = utils.formatBytes(size);
      _$(fileCard)
        .find(".upload-progress-status #fileSize")
        .text(`${maxValue}`);

      _$(fileCard).find(".progress-bar-container").removeClass("is-hidden");
    }
  },
  updateUploadFileProgressBar: ({ fileId, size, maxSize }) => {
    const fileCard = _$(`#${fileId}`).el();
    if (fileCard) {
      _$(fileCard).find(".progress").attr("value", size);
      _$(fileCard).find(".progress").attr("max", maxSize);

      const updatedValue = parseInt(size);

      const completedValue = utils.formatBytes(updatedValue);
      const maxValue = utils.formatBytes(maxSize);

      const percentage = Math.ceil((size / maxSize) * 100);

      _$(fileCard).find("#completed-percentage").text(`${percentage} %`);

      _$(fileCard)
        .find(".upload-progress-status #fileSizeUploaded")
        .text(`${completedValue}`);

      _$(fileCard)
        .find(".upload-progress-status #fileSize")
        .text(`${maxValue}`);

      _$(fileCard).find(".upload-progress-status").removeClass("is-hidden");
      _$(fileCard).find(".progress-bar-container").removeClass("is-hidden");
    }
  },
  toggleChunkPreparationStalled: (action, payload) => {
    const { fileId, chunkPreparationStalled } = payload;
    console.log("toggleChunkPreparationStalled", {
      fileId,
      chunkPreparationStalled,
    });
    const fileCard = _$(`#${fileId}`).el();
    if (fileCard) {
      _$(fileCard)
        .find(".chunk-preparation-stalled")
        .toggleClass("is-hidden", !chunkPreparationStalled);
    }
  },
  updateFileTimeStatus: ({
    fileId,
    speed,
    elapsedTime,
    timeRemaining,
    overAllSpeed,
  }) => {
    const fileCard = _$(`#${fileId}`).el();
    if (!fileCard) return;

    const uploadingSpeed = utils.formatBytes(speed);
    const _elapsedTime = utils.formatTime({
      secs: elapsedTime,
      minLabel: "m",
      secondsLabel: "s",
    });
    const _timeRemaining = timeRemaining
      ? utils.formatTime({
          secs: timeRemaining,
          minLabel: "m",
          secondsLabel: "s",
        })
      : "-";
    const _overAllSpeed = utils.formatBytes(
      Number.isFinite(overAllSpeed) ? overAllSpeed : 0
    );

    _$(fileCard)
      .find(".upload-progress-status #uploadingSpeed")
      .text(`${uploadingSpeed}/s`);

    _$(fileCard)
      .find(".upload-progress-status #elapsedTime")
      .text(`${_elapsedTime}`);

    _$(fileCard)
      .find(".upload-progress-status #remainingTime")
      .html(`${_timeRemaining}`);

    _$(fileCard)
      .find(".upload-progress-status #uploadingAverageSpeed")
      .html(`${_overAllSpeed || 0}/s`);

    _$(fileCard).find(".upload-progress-status").removeClass("is-hidden");
  },
};

const handleShowFileUploadStatus = (action, payload) => {
  fileProgressStatus.showUploadFileProgressBar(payload);
};

const handleUpdateFileUploadStatus = (action, payload) => {
  fileProgressStatus.updateUploadFileProgressBar(payload);
  updateGlobalProgressBar(payload.size);
};

const handleUpdateFileTimeStatus = (action, payload) => {
  fileProgressStatus.updateFileTimeStatus(payload);
  store.dispatch("updateGlobalUploadTime");
};

const handleUpdateFileStatusAndRerenderFile = (action, payload = {}) => {
  Object.values(payload).forEach((file) => {
    const { id, status } = file;

    if (!_$(`#${id}`).el()) return;

    if (status === "failed") {
      // totalFileStatus.setTotalFilesFailed(1);
    }

    store.dispatch("enableGlobalUpload");

    const showCardButtons = getButtonsToShowOnFileCard(status);
    const listingError = getListingError(file.listingError);
    const uploadingError = getUploadingError(status, file.uploadingError);
    const completionDetails = getUploadCompletionMessage(file);
    const progressBarStatus = getProgressBarStatus(file);
    const timeStatus = getUploadTimeStatus(file);

    const isElectronUploadOnlyApp = invoke("isElectron");

    _$(`#${id}`).html(
      uploadFileCardTemplate({
        hideCheckbox: isElectronUploadOnlyApp,
        checked: _$(`#${id}`).find(".checkbox", true)?.checked,
        image: getUploadItemIcon(file),
        name: file.nameF,
        sizeInBytes: file.file.size,
        size: file.sizeF,
        listingError,
        customFormData: file?.customFormData,
        lastModifiedF: file.lastModifiedF,
        uploadType: file?.uploadType,
        completionMessage: completionDetails?.message,
        timeTaken: completionDetails?.timeTaken,
        averageUploadingSpeed: completionDetails?.averageUploadingSpeed,
        uploadingError,
        finalizingMsg: file.finalizingMsg,
        ...showCardButtons,
        ...progressBarStatus,
        ...timeStatus,
        ...UploadFileCardTemplateTexts(),
      })
    );

    lookForAutoStartUpload(file, status);
    uploadFileCardButtonsListeners(file, _$(`#${id}`).el());
  });
};

// subscribe events
const subscribe = () => {
  const uploadFileActions = [
    {
      eventName: "addFileToUploadFileList",
      callback: handleUpdateUploadFileListing,
    },
    {
      eventName: "addFileToUploadFileListAndRefresh",
      callback: handleUpdateUploadFileListing,
    },
    { eventName: "showFileUploadStatus", callback: handleShowFileUploadStatus },
    {
      eventName: "updateFileUploadStatus",
      callback: handleUpdateFileUploadStatus,
    },
    { eventName: "updateFileTimeStatus", callback: handleUpdateFileTimeStatus },
    {
      eventName: "toggleChunkPreparationStalled",
      callback: fileProgressStatus.toggleChunkPreparationStalled,
    },
    {
      eventName: "updateFileStatusAndRerenderFile",
      callback: handleUpdateFileStatusAndRerenderFile,
    },
    {
      eventName: "removeFileFromUploadFileListing",
      callback: handleUpdateUploadFileListing,
    },
    {
      eventName: "removeMultipleFilesFromUploadFileListing",
      callback: handleUpdateUploadFileListing,
    },
    {
      eventName: "updateFileKeyAndId",
      callback: handleUpdateUploadFileListing,
    },
    {
      eventName: "scrollToCurrentUploadFile",
      callback: (action, payload) => {
        const scrollWithActivityUpload = _$("#scrollWithActivityUpload");
        const scrollWithActivityUploadWrapper = _$(
          "#scrollWithActivityUploadWrapper"
        );

        const { file } = payload;

        if (scrollWithActivityUploadWrapper.hasClass("is-hidden")) {
          scrollWithActivityUploadWrapper.show();
        }

        const canScrollUploadList = scrollWithActivityUpload?.el()?.checked;
        if (!canScrollUploadList || !file) {
          return;
        }

        const { id } = file;
        let index = -1;

        const fileList = Object.values(store.fileList);
        fileList.every((file) => {
          if (file.hide) {
            return true;
          }

          index++;

          if (file.id === id) {
            return false;
          }

          return true;
        });

        virtualScrollerList.scrollToIndex(index);
      },
    },
    {
      eventName: "fileUploadingQueueFinished",
      callback: (action, payload) => {
        const { canceled, completedFiles } = payload;

        const allFiles = Object.values(store.fileList);
        const completedUploadFiles = allFiles.filter(
          (file) => file.status !== "init"
        );
        const completedFileList = completedFiles.map((completedFile) => {
          const selectedFile = completedUploadFiles.find(
            (completedUploadFile) => completedUploadFile.id === completedFile.id
          );
          return { ...selectedFile };
        });

        const parentPostUploadConfig = {
          type: "uploadCompleted",
          files: completedFileList.map((file) => ({
            name: file.name,
            uploading_to: file.nameF,
            size: file.size,
            status: file.status,
          })),
        };

        invoke("uploadNotifications", [parentPostUploadConfig]);

        if (!canceled) {
          queueCompleted = true;

          const allUploadedFiles = allFiles.filter(
            (file) => file.status === "init"
          );

          if (allUploadedFiles.length === 0) {
            const redirectToAfterUpload = invoke("getUserCustomizationValue", [
              "redirectToAfterUpload",
              true,
            ]);

            if (redirectToAfterUpload) {
              invoke("redirectPage", [redirectToAfterUpload]);
            }
          }

          const showMessageAfterUpload = invoke("getUserCustomizationValue", [
            "showMessageAfterUpload",
          ]);

          if (showMessageAfterUpload) {
            _$("#uploadCompleteSection").removeClass("is-hidden");
          }
        }

        const scrollWithActivityUploadWrapper = _$(
          "#scrollWithActivityUploadWrapper"
        );
        scrollWithActivityUploadWrapper.addClass("is-hidden");
        // _$("#globalUploads").html("");
        // _$("#globalUploads").addClass("is-hidden");

        const closeModalOnFinishUpload = invoke("getUserCustomizationValue", [
          "upload_hideUploadBarAfterUpload",
        ]);
        const autoRemoveUploadedItemFromList = invoke(
          "getUserCustomizationValue",
          ["autoRemoveUploadedItemFromList"]
        );
        const autoShareUploadedItem = invoke("getUserCustomizationValue", [
          "autoShareUploadedItem",
        ]);
        const autoQuickShareUploadedItem = invoke("getUserCustomizationValue", [
          "autoQuickShareUploadedItem",
        ]);
        const disableAutohideUploadCompleteGrowl = invoke(
          "getUserCustomizationValue",
          ["noTimeoutUploadedNote"]
        );
        const notifyToShareOnUploadComplete = invoke(
          "getUserCustomizationValue",
          ["autoShareUploadedItemNotify"]
        );
        const notifyOnUploadComplete = invoke("getUserCustomizationValue", [
          "showUploadNotification",
        ]);
        const runBatchCompletedCommandAfterUploadQueueFinishes = invoke(
          "getUserCustomizationValue",
          ["runBatchCompletedCommandAfterUploadQueueFinishes"]
        );
        invoke("continueDownload");

        if (notifyOnUploadComplete) {
          const onPermissionGranted = () => {
            new Notification("Upload Completed", {
              body: "All files uploaded.",
            });
          };

          utils.sendBrowserNotification({ onPermissionGranted });
        }

        if (
          notifyToShareOnUploadComplete &&
          (autoShareUploadedItem || autoQuickShareUploadedItem)
        ) {
          const onPermissionGranted = () => {
            new Notification("Alert", {
              icon: `assets/images/favicon-logo.png`,
              body: "Your files are uploaded and ready to share",
            });
          };

          utils.sendBrowserNotification({ onPermissionGranted });
        }

        if (runBatchCompletedCommandAfterUploadQueueFinishes) {
          batchComplete()
            .then(() => {
              invoke("growl", [t("transferAcknowledged"), t("result"), 4000]);
            })
            .catch((err) => {
              invoke("growl", [err, "", 4000, true]);
            });
        }

        const isElectronUploadOnlyApp = invoke("isElectron");
        if (isElectronUploadOnlyApp && !canceled) {
          shareFilesOnQueueFinishedElectron();
        }

        if (autoShareUploadedItem || autoQuickShareUploadedItem) {
          const files = getSharingFiles();
          if (files.filesLength) {
            if (autoQuickShareUploadedItem) {
              invoke("initQuickSharing", [files]);
            } else if (autoShareUploadedItem) {
              invoke("initSharing", [files]);
            }
          }
        }

        // Auto remove item on complete customization
        if (autoRemoveUploadedItemFromList) {
          store.dispatch("removeMultipleFilesFromUploadFileListing", {
            status: "done",
          });
        }

        if (closeModalOnFinishUpload) {
          invoke("closeUploadModal");
        }

        invoke("refreshListing");

        if (canceled) {
          invoke("titleAlert", [` ** ${t(`uploadingCanceled`)} **`, 1000]);
          return;
        }

        invoke("titleAlert", [` ** ${t("uploadCompleted")} **`, 1000]);

        const dontShowUploadQueueCompletedGrowlMessage = invoke(
          "getUserCustomizationValue",
          ["dontShowUploadQueueCompletedGrowlMessage"]
        );

        if (
          !dontShowUploadQueueCompletedGrowlMessage &&
          !runBatchCompletedCommandAfterUploadQueueFinishes
        ) {
          invoke("growl", [
            "",
            t("uploadCompleted"),
            disableAutohideUploadCompleteGrowl ? null : 4000,
            false,
          ]);
        }
      },
    },
    {
      eventName: "enableGlobalUpload",
      callback: () => {
        const currentUploadingFile = uploadingQueue.getCurrentUploadingFile();
        const uploadingFiles = uploadingQueue.getGlobalUploadedFiles();
        const completedFiles = uploadingQueue.getCompletedFiles();

        if (uploadingFiles.length === 0) {
          return;
        }

        const curFile = store.fileList[currentUploadingFile.id];

        if (!curFile) {
          return;
        }

        let elapsedTime = curFile.elapsedTime;
        let totalBytesToUpload = 0;
        let totalBytesUploaded = curFile.uploadedBytes || 0;

        uploadingFiles.forEach((file) => {
          if (!file) return;
          const _file = store.fileList[file.id];
          elapsedTime += _file?.elapsedTime || 0;
          totalBytesToUpload += _file?.size || 0;
        });

        completedFiles.forEach((file) => {
          const _file = store.fileList[file.id];
          totalBytesUploaded += _file?.size || 0;
        });

        const { uploadedBytesFormatted, completedPercentage } =
          getProgressBarStatusGlobal({
            uploadedBytes: totalBytesUploaded,
            size: totalBytesToUpload,
          });

        const size = utils.formatBytes(totalBytesToUpload);
        const status = store.fileList[currentUploadingFile.id].status;
        const showCardButtons = getButtonsToShowOnFileCardGlobal(status);

        const formattedElapsedTime = utils.formatTime({
          secs: elapsedTime,
          minLabel: "m",
          secondsLabel: "s",
        });

        const averageSpeed = parseInt(totalBytesUploaded / elapsedTime);
        const formattedAverageSpeed = utils.formatBytes(
          Number.isFinite(averageSpeed) ? averageSpeed : 0
        );

        const timeRemaining =
          (totalBytesToUpload - totalBytesUploaded) / averageSpeed;

        const formattedTimeRemaining =
          timeRemaining && averageSpeed
            ? utils.formatTime({
                secs: timeRemaining,
                minLabel: "m",
                secondsLabel: "s",
              })
            : "-";

        _$("#globalUploads").html(
          globalFileCardTemplate({
            name: currentUploadingFile.nameF,
            currentUploadingFileNumber: completedFiles.length + 1,
            totalUploadingFiles: uploadingFiles.length,
            totalBytesToUpload,
            totalBytesUploaded,
            showProgressBar: true,
            remainingText: t("remaining"),
            uploadingText: t("uploading"),
            speedText: t("speed"),
            cancelText: t("cancel"),
            resumeText: t("resume"),
            pauseText: t("pause"),
            pausingText: t("pausing"),
            cancelingText: t("canceling"),
            averageSpeedText: t("averageSpeed"),
            elapsedText: t("elapsed"),
            skipText: t("skip"),
            completedPercentage,
            uploadedBytesFormatted,
            size,
            elapsedTime: formattedElapsedTime,
            timeRemaining: formattedTimeRemaining,
            overAllSpeed: formattedAverageSpeed,
            ...showCardButtons,
          })
        );

        _$("#globalUploads #skip-button").on("click", () => {
          const canceled = window.confirm(
            t("areYouSureYouWantToCancelParticularFileFromUploading", {
              file: currentUploadingFile.nameF,
            })
          );

          if (canceled) {
            uploadingQueue.cancel();
          }
        });

        _$("#globalUploads #cancel-button").on("click", () => {
          if (uploadingQueue.queue.length < 1) return;

          const canceled = window.confirm(
            t("areYouSureYouWantToCancelUploading")
          );

          if (canceled) {
            uploadingQueue.cancel(true);
          }
        });

        _$("#globalUploads #pause-button").on("click", () => {
          uploadingQueue.pausing();
        });

        _$("#globalUploads #resume-pause-button").on("click", () => {
          uploadingQueue.resume();
        });

        _$("#globalUploads").removeClass("is-hidden");
      },
    },
    {
      eventName: "updateCurrentFileGlobal",
      callback: (action, payload) => {
        _$("#current-uploading-file-global").text(payload.file.nameF);
      },
    },
    {
      eventName: "disableGlobalUpload",
      callback: () => {
        _$("#globalUploads").html("");
        _$("#globalUploads").addClass("is-hidden");
      },
    },
    {
      eventName: "updateGlobalUploadTime",
      callback: () => {
        const currentUploadingFile = uploadingQueue.getCurrentUploadingFile();
        const uploadingFiles = uploadingQueue.getGlobalUploadedFiles();
        const completedFiles = uploadingQueue.getCompletedFiles();

        const curFile = store.fileList[currentUploadingFile.id];

        let elapsedTime = curFile.elapsedTime;
        let totalBytesToUpload = 0;
        let bytesUploaded = curFile.uploadedBytes;

        uploadingFiles.forEach((file) => {
          const _file = store.fileList[file.id];
          totalBytesToUpload += _file?.size || 0;
        });

        completedFiles.forEach((file) => {
          const _file = store.fileList[file.id];
          bytesUploaded += _file?.size || 0;
          elapsedTime += _file?.elapsedTime || 0;
        });

        const formattedElapsedTime = utils.formatTime({
          secs: elapsedTime,
          minLabel: "m",
          secondsLabel: "s",
        });

        const averageSpeed = parseInt(bytesUploaded / elapsedTime);
        const formattedAverageSpeed = utils.formatBytes(
          Number.isFinite(averageSpeed) ? averageSpeed : 0
        );

        const timeRemaining =
          (totalBytesToUpload - bytesUploaded) / averageSpeed;

        const formattedTimeRemaining =
          timeRemaining && averageSpeed
            ? utils.formatTime({
                secs: timeRemaining,
                minLabel: "m",
                secondsLabel: "s",
              })
            : "-";

        _$("#globalUploads #elapsedTime").text(formattedElapsedTime);
        _$("#globalUploads #uploadingAverageSpeed").text(
          `${formattedAverageSpeed}/s`
        );
        _$("#globalUploads #remainingTime").text(formattedTimeRemaining);
      },
    },
  ];

  uploadFileActions.forEach((action) => {
    store.subscribe(action.eventName, action.callback);
  });
};

const getButtonsToShowOnFileCardGlobal = (status) => {
  let button = {
    skipButton: true,
    cancelButton: true,
  };

  const cardButtons = {
    uploading: {
      pauseButton: true,
    },
    pausing: {
      pausingButton: true,
    },
    canceling: {
      skipButton: false,
      cancelButton: false,
      cancelingButton: true,
    },
    paused: {
      resumePauseButton: true,
    },
  };

  const dynamicButtons = cardButtons[status] || { pauseButton: true };
  return { ...button, ...dynamicButtons };
};

const getProgressBarStatusGlobal = (file) => {
  const { uploadedBytes = 0, size } = file;
  let progressBarStatus = {};

  const updatedValue = parseInt(uploadedBytes);

  progressBarStatus.uploadedBytesFormatted = utils.formatBytes(updatedValue);
  progressBarStatus.uploadedBytes = updatedValue;

  const percent = updatedValue / size;

  progressBarStatus.completedPercentage = `${Math.ceil(
    (isNaN(percent) ? 0 : percent) * 100
  )} %`;

  return progressBarStatus;
};

const updateGlobalProgressBar = (size) => {
  const uploadingFiles = uploadingQueue.getGlobalUploadedFiles();
  const completedFiles = uploadingQueue.getCompletedFiles();

  let totalBytesToUpload = 0;
  let totalBytesUploaded = size || 0;

  uploadingFiles.forEach((file) => {
    const fileSize = file.size || 0;
    totalBytesToUpload += fileSize;
  });

  completedFiles.forEach((file) => {
    const fileSize = file.size || 0;
    totalBytesUploaded += fileSize;
  });

  fileProgressStatus.updateUploadFileProgressBar({
    fileId: "globalUploads",
    size: totalBytesUploaded,
    maxSize: totalBytesToUpload,
  });
};

const addFileThroughGlobalButton = (files) => {
  if (uploadingQueue.queue.length > 0 && isMaxFileErrorActive) {
    invoke("growl", [
      t("maxNumberOfFilesExceeded"),
      t("maxFileAllowedTouploadExceeded", { maxFilesAllowedInQueue }),
      4000,
      true,
    ]);
    return;
  }

  const filesToUpload = convertFilesObjectToList(files);
  addFilesToQueue(filesToUpload);
};

//On DOM Ready
_$("body").ready(async () => {
  const isElectronUploadOnlyApp = invoke("isElectron");

  //initialize theme
  const theme = invoke("getCurrentTheme");
  _$("html").attr("data-theme", theme);

  const newLocale = invoke("getCurrentLanguage");
  setLocale({ newLocale });

  // subscribe events for store
  subscribe();
  // upload modal header button events
  uploadHeaderButtonsEvents();
  // upload drag and drop events
  uploadDragAndDropEvents();
  // initializing dropdown instance
  new Dropdown({});

  // Virtual Scroller For List View
  virtualScrollerList = new VirtualScroller({
    width: "100%",
    itemHeight: isElectronUploadOnlyApp ? 100 : 80,
    maxRowsToShow: 10,
    topOffset: 0,
  });

  const disableShareForUploadedItem = invoke("getUserCustomizationValue", [
    "disableShareForUploadedItem",
  ]);

  if (disableShareForUploadedItem)
    _$("#shareUploadedButtonUploadModalParent").remove();

  const commonUploadForm = invoke("commonUploadForm");

  if (!commonUploadForm?.formDetails) {
    _$("#commonUploadFormButton").remove();
  } else {
    _$("#commonUploadFormButton").removeClass("is-hidden");
  }

  _$("#uploadCompleteSection .close-button").on("click", () => {
    _$("#uploadCompleteSection").addClass("is-hidden");
  });

  _$("#uploadCompleteSection").on("click", (e) => {
    const clickedOnUploadCompleteChip = _$(
      "#uploadCompleteSection .upload-complete-chip"
    )
      .el()
      .contains(e.target);
    if (clickedOnUploadCompleteChip) {
      return;
    }

    _$("#uploadCompleteSection").addClass("is-hidden");
  });

  // message event from parent document to this iframe
  window.addEventListener("message", function (event) {
    const { key, value } = event.data;
    switch (key) {
      case "theme":
        _$("html").attr("data-theme", value);
        break;
      case "language":
        setLocale(value);
        break;
      case "open":
        window.init_href_path = value;
        if (Object.keys(store.fileList).length === 0)
          _$("#addFilesButtonUploadModal").el().click();
        break;
      case "continueUpload":
        uploadingQueue.continueUpload();
        break;
      case "refreshUploadList":
        reCheckFileQuota();
        break;
      case "addFilesToQueue":
        addFileThroughGlobalButton(value);
        break;
      case "overrideFontFamily":
        document.documentElement.style.setProperty("--font-family", value);
        break;
      case "applyTemporaryTheme":
        const temporaryCSSTheming = _$("#temporaryCSSTheming").el();

        if (temporaryCSSTheming) {
          temporaryCSSTheming.textContent = `${value}`;
        } else {
          const dynamicStyle = document.createElement("style");
          dynamicStyle.textContent = `${value}`;
          dynamicStyle.id = "temporaryCSSTheming";
          document.head.appendChild(dynamicStyle);
        }
        break;
      default:
        break;
    }
  });
});
